<script setup lang="ts">
import type { UIChipsWrapperProps } from './UIChipsWrapper.props'
import { dragscroll as vDragscroll } from 'vue-dragscroll'

const props = defineProps<UIChipsWrapperProps>()

const emit = defineEmits<{ selected: [index: number] }>()

const { breakpoint } = useProjectBreakpoint()
const isDesktop = breakpoint.greater('md')

const selectedChip = ref(props.defaultSelectedChip ?? null)

const changeSelectedChip = (index: number) => {
  selectedChip.value = index
}

defineExpose({ changeSelectedChip })

const handleSelectChip = (index: number) => {
  emit('selected', index)
  if (props.disableInternalState) {
    return
  }
  changeSelectedChip(index)
}

const containerRef = ref<HTMLElement | null>(null)
const chipRefs = ref<(HTMLElement | null)[]>([])

const scrollToCenter = () => {
  if (containerRef.value && chipRefs.value[selectedChip.value]) {
    const container = containerRef.value
    const chip = chipRefs.value[selectedChip.value]?.children[0]
    if (!container || !chip) {
      return
    }
    const containerRect = container.getBoundingClientRect()
    const chipRect = chip.getBoundingClientRect()

    // + containerRect.left / 2 for right overflow

    const offset =
      chipRect.left -
      (containerRect.left + containerRect.width / 2) +
      chipRect.width / 2 +
      containerRect.left / 2

    container.scrollBy({
      left: offset,
      behavior: 'smooth',
    })
  }
}

watch(selectedChip, () => {
  nextTick(() => {
    scrollToCenter()
  })
})

onMounted(() => {
  nextTick(() => {
    scrollToCenter()
  })

  useEventListener(containerRef, 'scroll', () => {
    const maxScrollLeft =
      containerRef.value!.scrollWidth - containerRef.value!.clientWidth

    if (containerRef.value!.scrollLeft > maxScrollLeft) {
      containerRef.value!.scrollLeft = maxScrollLeft
    }

    if (containerRef.value!.scrollLeft < 0) {
      containerRef.value!.scrollLeft = 0
    }
  })
})
</script>

<template>
  <div
    ref="containerRef"
    v-dragscroll.x="{ active: isDesktop }"
    class="scrollbar-hide flex gap-6 overflow-x-scroll"
  >
    <div
      v-for="(singleChip, index) in chips"
      :ref="el => (chipRefs[index] = el as HTMLElement)"
      :key="index"
      class="chip flex min-w-fit gap-6 first:ml-auto last:mr-auto"
      :class="{ '[&:last-child>span]:hidden': isTimelineStyle }"
    >
      <UIChips
        v-bind="singleChip"
        :selected="index === selectedChip"
        :is-timeline-style="isTimelineStyle"
        @select="handleSelectChip(index)"
      />
      <span v-if="isTimelineStyle"> &bull; </span>
    </div>
  </div>
</template>
